import {
  api,
  ApiError,
  type BooDiscordStatus,
  type BooGchatStatus,
  type BooSlackStatus,
  type BooTeamsStatus,
  type ContainerRow,
} from "@/lib/api";
import { booAppState } from "@/lib/connection-status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ConnectionId } from "./connections-lib";

// The Boo app of a chat platform (Slack, Teams, Google Chat, Discord): one
// install per workspace, answering through one Boo at a time. Attaching needs
// an admin of this Boo, and taking it from another Boo needs one of that Boo's
// owners, so a move is offered only when the viewer holds both.

export type ChatPlatform = "slack" | "teams" | "gchat" | "discord";
export type BooAppStatus = BooSlackStatus | BooTeamsStatus | BooGchatStatus | BooDiscordStatus;

export const BOO_APPS: Record<
  ChatPlatform,
  { label: string; connection: ConnectionId; placeNoun: string; place: (s: BooAppStatus) => string | null }
> = {
  slack: { label: "Slack", connection: "slack", placeNoun: "Slack workspace", place: (s) => (s as BooSlackStatus).teamName },
  teams: {
    label: "Microsoft Teams",
    connection: "microsoft-teams",
    placeNoun: "Teams tenant",
    place: (s) => (s as BooTeamsStatus).tenantName,
  },
  gchat: {
    label: "Google Chat",
    connection: "google-chat",
    placeNoun: "Google Workspace",
    place: (s) => (s as BooGchatStatus).customerName,
  },
  discord: {
    label: "Discord",
    connection: "discord",
    placeNoun: "Discord server",
    place: (s) => (s as BooDiscordStatus).guildName,
  },
};

export function useBooApp(platform: ChatPlatform, container: ContainerRow, { enabled = true } = {}) {
  const queryClient = useQueryClient();
  const config = BOO_APPS[platform];
  const key = [`${platform}-boo-status`];
  const query = useQuery({ queryKey: key, queryFn: (): Promise<BooAppStatus> => api[platform].boo.status(), enabled });
  const boos = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const returnTo = `/orgs/${container.organizationId}/containers/${container.id}/connections/${config.connection}`;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ["containers"] });
  };

  const install = useMutation({
    mutationFn: () => api[platform].boo.install(container.id, returnTo),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => {
      if (platform === "discord" && error instanceof ApiError && error.code === "brain_update_required") {
        toast.error("This Boo can't join Discord yet — it becomes available with its next update.");
      }
    },
  });
  const move = useMutation({
    mutationFn: async (): Promise<void> => {
      await api[platform].boo.move(container.id);
    },
    onSuccess: refresh,
    onError: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const disconnect = useMutation({
    mutationFn: async (): Promise<void> => {
      await api[platform].boo.disconnect();
    },
    onSuccess: () => {
      // Reflect the disconnect in the cache right away: a bare invalidate
      // refetches async, so the page showed the stale connected state until a
      // second click. The backend keeps the install and unlinks the Boo.
      queryClient.setQueryData<BooAppStatus>(key, (prev) => (prev ? { ...prev, containerId: null } : prev));
      refresh();
    },
  });
  const release = useMutation({
    mutationFn: async (): Promise<void> => {
      await api[platform].boo.release();
    },
    onSuccess: refresh,
  });

  const data = query.data;
  const holderId = data?.containerId ?? null;
  const holder = boos.data?.data.find((b) => b.id === holderId);
  const canAttach = container._permissions?.canAttachWorkspace === true;
  // A holder missing from the list was deleted, so there is nothing to release.
  const canMoveHere =
    canAttach && (!holderId || holderId === container.id || !holder || holder._permissions?.canReleaseWorkspace === true);

  return {
    platform,
    config,
    query,
    data,
    state: booAppState(data, container.id),
    place: (data && config.place(data)) ?? null,
    holderName: data?.containerName ?? "another Boo",
    install,
    move,
    disconnect,
    release,
    canAttach,
    canMoveHere,
    canInstall: data?._permissions?.canInstall === true && canAttach,
    canDisconnect: data?._permissions?.canDisconnect === true,
    canRelease: container._permissions?.canReleaseWorkspace === true,
  };
}

export type BooApp = ReturnType<typeof useBooApp>;
